import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

export interface WorkdaySyncReportDetail {
    requestId: number;
    requestSeqId: number;
    requestDt: string | null;
    employeeId: string | null;
    employeeNm: string | null;
    requestType: string | null;
    directReportOnly: boolean | null;
    requiredSkill: string | null;
    requiredTeam: string | null;
    startTime: string | null;
    endTime: string | null;
    requiredNotes: string | null;
    requestedHours: number | null;
    timeOffCd: string | null;
    wid: string | null;
    syncedAt: string | null;
}

interface WorkdaySyncReportState {
    workdaySyncData: WorkdaySyncReportDetail[];
    loading: boolean;
    error: string | null;
}

const initialState: WorkdaySyncReportState = {
    workdaySyncData: [],
    loading: false,
    error: null,
};

export const fetchWorkdaySyncReport = createAsyncThunk(
    'workdaySyncReport/fetchWorkdaySyncReport',
    async (
        { siteCd, startDate, endDate }: { siteCd: string; startDate?: string; endDate?: string },
        { rejectWithValue }
    ) => {
        try {
            const params: string[] = [`siteCd=${siteCd}`];
            if (startDate) params.push(`startDate=${startDate}`);
            if (endDate) params.push(`endDate=${endDate}`);

            const url = `${ENDPOINTS.GET_WORKDAY_SYNC_REPORT}?${params.join('&')}`;
            const response = await API_FETCH(url);
            return response;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch Workday sync report data');
        }
    }
);

const workdaySyncReportSlice = createSlice({
    name: 'workdaySyncReport',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkdaySyncReport.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchWorkdaySyncReport.fulfilled, (state, action) => {
                state.loading = false;
                state.workdaySyncData = Array.isArray(action.payload) ? action.payload : [];
            })
            .addCase(fetchWorkdaySyncReport.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = workdaySyncReportSlice.actions;
export default workdaySyncReportSlice.reducer;
